import { graphMutation, graphQuery } from '@services/commService';
import { MentorFormTemplate, MentorFormActivity, SurveyResponse } from './types';

export const getMentorForms = async (userId: string): Promise<MentorFormActivity[]> => {
  const query = `
    query GetMentorFormsActivity($userId: [String]) {
      mentorForms: mentorFormsActivity(userId: $userId) {
        id
        formId
        createdBy {
          id
          fullName
        }
        name
        surveySchema
        surveyResponse
        createdAt
        updatedAt
      }
    }
  `;

  const response = await graphQuery(query, true, { userId: [userId] });
  const forms = response?.data?.mentorForms;

  if (!Array.isArray(forms)) {
    throw new Error('Failed to fetch mentor forms');
  }

  return forms;
};

export const getMentorFormTemplates = async (): Promise<MentorFormTemplate[]> => {
  const query = `
    query GetMentorFormTemplates {
      mentorForms {
        id
        name
        surveySchema
      }
    }
  `;

  const response = await graphQuery(query, true);
  const forms = response?.data?.mentorForms;

  if (!Array.isArray(forms)) {
    throw new Error('Failed to fetch mentor form templates');
  }

  return forms;
};

export const createMentorForm = async (
  userId: string,
  formId: string,
  surveyResponse: SurveyResponse,
): Promise<string> => {
  const mutation = `
    mutation CreateMentorFormActivity(
      $userId: String!
      $formId: String!
      $surveyResponse: JSON!
    ) {
      createMentorFormActivity(
        userId: $userId
        formId: $formId
        surveyResponse: $surveyResponse
      ) {
        id
      }
    }
  `;

  const response = await graphMutation(mutation, { userId, formId, surveyResponse }, true);
  const id = response?.data?.createMentorFormActivity?.id;

  if (!id) {
    throw new Error('Failed to create mentor form');
  }

  return id;
};

export const updateMentorForm = async (id: string, surveyResponse: SurveyResponse): Promise<MentorFormActivity> => {
  const mutation = `
    mutation UpdateMentorFormActivity(
      $id: String!
      $surveyResponse: JSON!
    ) {
      updateMentorFormActivity(
        id: $id
        surveyResponse: $surveyResponse
      ) {
        id
        updatedAt
        surveyResponse
      }
    }
  `;

  const response = await graphMutation(
    mutation,
    {
      id,
      surveyResponse,
    },
    true,
  );
  const updatedForm = response?.data?.updateMentorFormActivity;

  if (!updatedForm?.id) {
    throw new Error('Failed to update mentor form');
  }

  return updatedForm;
};

export const deleteMentorForm = async (id: string): Promise<string> => {
  const mutation = `
    mutation DeleteMentorFormActivity($id: String!) {
      deleteMentorFormActivity(id: $id) {
        id
      }
    }
  `;

  const response = await graphMutation(mutation, { id }, true);
  const deletedId = response?.data?.deleteMentorFormActivity?.id;

  if (!deletedId) {
    throw new Error('Failed to delete mentor form');
  }

  return deletedId;
};
